/**
 * Assassin Workshop — Mon Atelier local bootstrap
 * Lot 8 / v1.2.0
 * Lightweight analytics + local label guard + lazy dashboard engine.
 */
(() => {
  'use strict';

  const bootstrapScript = document.currentScript;
  const roots = Array.from(document.querySelectorAll('[data-aw-atelier]'));
  if (!roots.length || window.AWMonAtelierBootstrap?.version) return;

  const eventKey = (value = '') => String(value).trim().toLowerCase().normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');
  const ensureAnalytics = () => {
    if (window.AWAnalytics?.track) return;
    const themeRole = String(window.Shopify?.theme?.role || '').toLowerCase();
    const preview = Boolean(window.Shopify?.designMode || (themeRole && themeRole !== 'main') || /(?:\?|&)preview_theme_id=/.test(location.search) || /(?:\?|&)previewPath=/.test(location.search));
    const safe = (value) => {
      if (value === null || value === undefined) return undefined;
      if (typeof value === 'boolean' || typeof value === 'number') return value;
      if (typeof value === 'string') return value.slice(0, 180);
      return undefined;
    };
    const track = (name, payload = {}) => {
      const event = eventKey(name);
      if (!event) return;
      const extra = Object.entries(payload).reduce((result, [key, value]) => {
        const cleanKey = eventKey(key).slice(0, 60), cleanValue = safe(value);
        if (cleanKey && cleanValue !== undefined) result[cleanKey] = cleanValue;
        return result;
      }, {});
      const data = { schema_version: 1, path: location.pathname.slice(0, 180), locale: document.documentElement.lang || '', currency: window.Shopify?.currency?.active || '', preview_mode: preview, ...extra };
      const fullName = `assassin_workshop:${event}`;
      window.dispatchEvent(new CustomEvent('aw:analytics', { detail: { event: fullName, payload: data, suppressed: preview } }));
      if (preview) return;
      const publish = (attempt = 0) => {
        try { if (window.Shopify?.analytics?.publish) { window.Shopify.analytics.publish(fullName, data); return; } } catch (_) { return; }
        if (attempt < 8) setTimeout(() => publish(attempt + 1), 250);
      };
      publish();
      try { window.dataLayer = window.dataLayer || []; window.dataLayer.push({ event: `aw_${event}`, ...data }); } catch (_) {}
    };
    window.AWAnalytics = { version: 'atelier-lite-1.0.0', track, preview };
  };
  ensureAnalytics();
  const track = (name, payload = {}) => { try { window.AWAnalytics?.track?.(name, payload); } catch (_) {} };
  const readJson = (key, fallback = []) => { try { const value = JSON.parse(localStorage.getItem(key) || 'null'); return value ?? fallback; } catch (_) { return fallback; } };
  const projectSnapshot = (root, id) => {
    const projects = readJson(root.dataset.savedKey || 'aw_airsoft_saved_configurations', []);
    const project = Array.isArray(projects) ? projects.find((item) => String(item.id) === String(id)) : null;
    if (!project) return {};
    return { platform: project.platform || '', manufacturer: project.manufacturer || '', replica_type: project.type || '', goal: project.goal || '', selected_count: Array.isArray(project.selected) ? project.selected.length : 0, total_cents: Number(project.total || 0), budget_cents: Number(project.budget || 0) };
  };
  roots.forEach((root) => {
    setTimeout(() => {
      const projects = readJson(root.dataset.savedKey || 'aw_airsoft_saved_configurations', []);
      const history = readJson(root.dataset.historyKey || 'aw_airsoft_purchase_history', []);
      track('atelier_viewed', { project_count: Array.isArray(projects) ? projects.length : 0, local_history_count: Array.isArray(history) ? history.length : 0 });
    }, 0);
    root.addEventListener('click', (event) => {
      const tab = event.target.closest?.('[data-aw-atelier-tab]');
      if (tab) track('atelier_tab_viewed', { tab: tab.dataset.awAtelierTab || '' });
      const compare = event.target.closest?.('[data-aw-project-compare]');
      if (compare) track('atelier_compare_started', projectSnapshot(root, compare.dataset.awProjectCompare));
      const continueLink = event.target.closest?.('.aw-atelier__project-actions a.button');
      if (continueLink) {
        const id = continueLink.closest('[data-aw-project]')?.dataset.awProject || '';
        track('atelier_continue_configuration', projectSnapshot(root, id));
      }
      const recommendation = event.target.closest?.('[data-aw-atelier-recommendations] a');
      if (recommendation) {
        const handle = String(recommendation.getAttribute('href') || '').match(/\/products\/([^/?#]+)/i)?.[1] || '';
        track('atelier_recommendation_clicked', { product_handle: handle });
      }
      const newConfiguration = event.target.closest?.('a.button[href*="configurateur-airsoft"]');
      if (newConfiguration && !continueLink) track('atelier_new_configuration_clicked', {});
    });
  });
  document.addEventListener('click', (event) => {
    const node = event.target?.closest?.('[data-aw-p1-track]');
    if (node) track(node.dataset.awP1Track || 'p1_click', { cluster: node.dataset.awP1Cluster || '', href: node.getAttribute('href') || '', label: (node.textContent || '').trim().slice(0, 120) });
  }, true);

  let loaded = false, loading = null, replaying = false;
  const coreSrc = (() => {
    try { const url = new URL(bootstrapScript?.src || '', location.href); url.pathname = url.pathname.replace(/aw-mon-atelier\.js$/, 'aw-mon-atelier-core.js'); return url.href; } catch (_) { return ''; }
  })();
  const normalizeBudgetLabels = (root) => {
    root.querySelectorAll('dt,th').forEach((node) => {
      const label = (node.textContent || '').trim();
      if (label === 'Marge' || label === 'Écart budget') node.textContent = 'Écart au budget';
    });
  };
  const installLabelGuard = (root) => {
    if (root.dataset.awAtelierLabelGuard === 'true') return;
    root.dataset.awAtelierLabelGuard = 'true';
    normalizeBudgetLabels(root);
    let scheduled = false;
    const observer = new MutationObserver(() => {
      if (scheduled) return;
      scheduled = true;
      queueMicrotask(() => { scheduled = false; normalizeBudgetLabels(root); });
    });
    observer.observe(root, { childList: true, subtree: true, characterData: true });
  };
  roots.forEach(installLabelGuard);
  const initialiseLate = () => {
    if (document.readyState === 'loading') return;
    roots.forEach((root) => {
      if (root.dataset.awReady === 'true') return;
      const scope = root.closest('.shopify-section') || root.parentElement || document;
      scope.dispatchEvent(new CustomEvent('shopify:section:load', { bubbles: true, detail: { awLazyBootstrap: true } }));
    });
  };
  const load = () => {
    if (loaded || roots.every((root) => root.dataset.awReady === 'true')) { loaded = true; return Promise.resolve(); }
    if (loading) return loading;
    if (!coreSrc) return Promise.reject(new Error('Mon Atelier core URL unavailable'));
    loading = new Promise((resolve, reject) => {
      const node = document.createElement('script'); node.src = coreSrc; node.async = true; node.dataset.awMonAtelierCore = 'true';
      node.addEventListener('load', () => { loaded = true; initialiseLate(); roots.forEach((root) => { root.dataset.awMonAtelierCoreLoaded = 'true'; normalizeBudgetLabels(root); }); resolve(); }, { once: true });
      node.addEventListener('error', () => { loading = null; reject(new Error('Mon Atelier core failed to load')); }, { once: true });
      document.head.appendChild(node);
    });
    return loading;
  };
  const warm = () => { load().catch(() => {}); };
  roots.forEach((root) => {
    if (root.dataset.awMonAtelierBootstrap === 'true') return;
    root.dataset.awMonAtelierBootstrap = 'true';
    root.addEventListener('pointerover', warm, { once: true, passive: true });
    root.addEventListener('pointerdown', warm, { once: true, passive: true });
    root.addEventListener('touchstart', warm, { once: true, passive: true });
    root.addEventListener('focusin', warm, { once: true });
    root.addEventListener('click', (event) => {
      if (loaded || replaying || event.defaultPrevented) return;
      const target = event.target?.closest?.('button,[role="button"]');
      if (!target || !root.contains(target)) return;
      event.preventDefault(); event.stopImmediatePropagation();
      load().then(() => { replaying = true; target.click(); replaying = false; }).catch(() => {});
    }, true);
    root.addEventListener('change', (event) => {
      if (loaded || replaying) return;
      const target = event.target;
      load().then(() => { if (!target?.dispatchEvent) return; replaying = true; target.dispatchEvent(new Event('change', { bubbles: true })); replaying = false; }).catch(() => {});
    }, { capture: true, once: true });
  });
  const hasStoredWorkshopData = roots.some((root) => {
    const keys = [root.dataset.savedKey, root.dataset.historyKey].filter(Boolean);
    try { return keys.some((key) => localStorage.getItem(key)); } catch (_) { return false; }
  });
  const hasShopifyWorkshopOrders = roots.some((root) => Boolean(root.querySelector('[data-aw-shopify-order-item]')));
  window.AWMonAtelierBootstrap = { version: '1.2.0', load, get loaded() { return loaded; } };
  if (hasStoredWorkshopData || hasShopifyWorkshopOrders || window.Shopify?.designMode) warm();
})();
